import { useState, useEffect } from 'react';
import { readerApi } from '../services/api';
import ReaderCard from '../components/readers/ReaderCard';
import CreateReaderForm from '../components/readers/CreateReaderForm';
import LoadingSpinner from '../components/LoadingSpinner';
import './ReadersPage.css';

const ReadersPage = () => {
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadReaders();
  }, []);

  const loadReaders = async () => {
    try {
      setLoading(true);
      const response = await readerApi.getReaders();
      setReaders(response.data.readers || []);
      setError(null);
    } catch (err) {
      console.error('Error loading readers:', err);
      setError(err.response?.data?.error || 'Failed to load readers');
    } finally {
      setLoading(false);
    }
  };

  const handleReaderCreated = (newReader) => {
    setReaders(prev => [...prev, newReader]);
    setShowCreateForm(false);
  };

  const handleReaderUpdated = (updatedReader) => {
    setReaders(prev => 
      prev.map(reader => 
        reader.id === updatedReader.id ? updatedReader : reader
      )
    );
  };

  const handleReaderDeleted = async (readerId) => {
    if (!window.confirm('Are you sure you want to delete this reader? This action cannot be undone.')) {
      return;
    }

    try {
      await readerApi.deleteReader(readerId);
      setReaders(prev => prev.filter(reader => reader.id !== readerId));
    } catch (err) {
      console.error('Error deleting reader:', err);
      setError(err.response?.data?.error || 'Failed to delete reader');
    }
  };

  const primaryReader = readers.find(reader => reader.isPrimary);
  const additionalReaders = readers.filter(reader => !reader.isPrimary);

  if (loading) {
    return <LoadingSpinner size="large" message="Loading readers..." />;
  }

  return (
    <div className="readers-page">
      <div className="readers-header">
        <h2>Readers</h2>
        <p>You can log reading for any reader on your account.</p>
        
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          Add reader
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="readers-content">
        {/* Primary Reader Section */}
        {primaryReader && (
          <div className="readers-section">
            <h3>Primary Reader</h3>
            <p className="section-description">
              This is your main reader account, created when you registered.
            </p>
            <div className="readers-grid">
              <ReaderCard 
                reader={primaryReader}
                onUpdate={handleReaderUpdated}
                onDelete={handleReaderDeleted}
                isPrimary={true}
              />
            </div>
          </div>
        )}

        {/* Additional Readers Section */}
        <div className="readers-section">
          <h3>Additional Readers</h3>
          <p className="section-description">
            Add family members or others you want to track reading for.
          </p>
          
          {additionalReaders.length === 0 ? (
            <div className="empty-state">
              <h4>No additional readers</h4>
              <p>Add family members or others to track their reading progress.</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
              >
                Add a reader
              </button>
            </div>
          ) : (
            <div className="readers-grid">
              {additionalReaders.map(reader => (
                <ReaderCard 
                  key={reader.id}
                  reader={reader}
                  onUpdate={handleReaderUpdated}
                  onDelete={handleReaderDeleted}
                  isPrimary={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Reader Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <CreateReaderForm 
              onReaderCreated={handleReaderCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadersPage;
